// The client `ReplayControl` transport contract (replay-viewer tasks 8.4;
// client-replay "The ReplayControls transport drives playback via ReplayControl
// messages"). This is the canonical, non-test home for the seam the `ReplayControls`
// component emits through — `src/test/fake-replay-control-transport.ts` re-exports these
// types (the `net/replay-http.ts` ⟷ `fake-replay-open-client.ts` precedent group 7 set),
// so production code never imports a test double.
//
// A verb maps 1:1 to the `waggle-proto` client→server `ReplayControl` message
// (PROTOCOL_VERSION 4, tasks 2.2): pause / resume (play) / set_speed{multiplier} /
// restart. There is NO seek verb — the playback-position strip is read-only and
// arbitrary seek is deferred (D-123); the only reposition is restart (re-run from tick 0).
//
// The *production wire-forwarding* transport (a `createReplayControlTransport(connection)`
// that encodes the `ReplayControl` client message and calls the session `Connection`'s
// send) plus the client `PROTOCOL_VERSION` 3→4 bump land with the live Match/replay
// composition and are proven at the opt-in live-interop gate (task 10.6) — exactly as
// group 7 deferred mounting `ReplayEntry` into the live App composition. Group 8 delivers
// the seam contract + the UI that emits through it (unit-covered against the fake
// transport); the seam is the tested boundary.

/** A replay playback-transport verb (the `waggle-proto` `ReplayControl` message,
 * tasks 2.2): `multiplier` is the client-requested speed; the server clamps it to
 * `replay_speed_max`. No seek verb (read-only position strip; D-123). */
export type ReplayControl =
  | { kind: 'pause' }
  | { kind: 'resume' }
  | { kind: 'setSpeed'; multiplier: number }
  | { kind: 'restart' };

/** The seam `ReplayControls` emits through — production forwards to the session
 * `Connection`'s `ReplayControl` send (deferred to the live composition, task 10.6);
 * tests inject a recorder. */
export interface ReplayControlTransport {
  sendReplayControl(control: ReplayControl): void;
}

/** A no-op inert default (the `INERT_LOD`/`INERT_CONTROLLER` "safe inert default"
 * precedent): the still-unwired production replay route supplies this so it keeps
 * compiling and never throws — it just emits nothing until the composition wires a
 * real wire-forwarding transport (task 10.6). */
export const INERT_REPLAY_CONTROL_TRANSPORT: ReplayControlTransport = {
  sendReplayControl: () => {
    // Pre-wiring production default: no replay connection to forward to yet.
  },
};

/** The minimal session-connection surface the production transport forwards to — the
 * `Connection.sendReplayControl` method (`net/wire/connection.ts`). Kept structural so
 * this seam module stays decoupled from the concrete `Connection`; a real `Connection`
 * satisfies it (its `sendReplayControl` accepts the identically-shaped wire
 * `ReplayControlMessage`). */
export interface ReplayControlWireSender {
  sendReplayControl(control: ReplayControl): unknown;
}

/** The **production** wire-forwarding `ReplayControlTransport` (replay-viewer task 10.6):
 * each `ReplayControls`-emitted verb is forwarded verbatim to the live replay session
 * `Connection`, which encodes it as the `waggle-proto` `ReplayControl` client message
 * (tag 8; `codec.test.ts golden_replay_control`) and writes it to the socket. The server
 * re-checks current-connection + accepted-`Subscribe` before applying any verb, so
 * client message ordering is not load-bearing (server-3.8 F10/F12/F13/F14). This replaces
 * `INERT_REPLAY_CONTROL_TRANSPORT` once the live Match/replay composition holds a
 * `Connection`; the whole path is exercised at the opt-in live-interop gate. */
export function createReplayControlTransport(
  connection: ReplayControlWireSender,
): ReplayControlTransport {
  return {
    sendReplayControl(control: ReplayControl): void {
      connection.sendReplayControl(control);
    },
  };
}

/** The client-side mirror of the server `Config::admission.replay_speed_max` seed
 * (design D6; seed 8×). The client cannot read server config, so the speed selector's
 * ceiling is this mirrored constant — advisory only (the server clamps authoritatively),
 * exactly as `Match.tsx`'s `TICK_RATE_HZ`/`MATCH_DURATION_MIN` mirror DESIGN §14 seeds. */
export const REPLAY_SPEED_MAX = 8;
